// ---------------------------------------------------------------------------
// SearchPanel — Global search across all blocks and pages.
//
// Searches element type, text content, element ID, and block names.
// Results are grouped by block name. Clicking a result selects it.
// ---------------------------------------------------------------------------

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { Search, X, ArrowLeft } from 'lucide-react'
import type { CogoElement, CogoBlockDef, CogoAppDef } from '@/features/page-editor/store/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SearchResult {
  blockName: string
  element: FlatElement
  /** Which page(s) contain this block */
  pageId: string | null
}

interface FlatElement {
  id: string
  type: string
  text?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Recursively flatten a CogoElement tree into a flat list */
function flattenElementTree(root: CogoElement): FlatElement[] {
  const result: FlatElement[] = []
  function walk(el: CogoElement) {
    result.push({ id: el.id, type: el.type, text: el.text })
    if (el.children) {
      for (const child of el.children) {
        walk(child)
      }
    }
  }
  walk(root)
  return result
}

/** Find which page contains a given block name */
function findPageForBlock(app: CogoAppDef, blockName: string): string | null {
  for (const page of app.pages) {
    if (page.blocks.includes(blockName)) {
      return page.id
    }
  }
  return null
}

/** Search across all blocks for elements matching the query */
function searchElements(
  blocks: Record<string, CogoBlockDef>,
  app: CogoAppDef | null,
  query: string,
): SearchResult[] {
  const results: SearchResult[] = []
  const q = query.toLowerCase()

  for (const [blockName, block] of Object.entries(blocks)) {
    const elements = flattenElementTree(block.ui.root)
    for (const el of elements) {
      if (
        el.type.toLowerCase().includes(q) ||
        (el.text && el.text.toLowerCase().includes(q)) ||
        el.id.toLowerCase().includes(q) ||
        blockName.toLowerCase().includes(q)
      ) {
        results.push({
          blockName,
          element: el,
          pageId: app ? findPageForBlock(app, blockName) : null,
        })
      }
    }
  }
  return results
}

/** Highlight matching portions of text */
function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>

  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const idx = lowerText.indexOf(lowerQuery)

  if (idx === -1) return <>{text}</>

  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-yellow-200/60 dark:bg-yellow-500/30 rounded-sm">
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  )
}

// ---------------------------------------------------------------------------
// SearchPanel Component
// ---------------------------------------------------------------------------

export function SearchPanel() {
  const blocks = useEditorStore((s) => s.blocks)
  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-focus input when panel opens
  useEffect(() => {
    // Small delay to ensure panel is rendered
    const timer = setTimeout(() => {
      inputRef.current?.focus()
    }, 50)
    return () => clearTimeout(timer)
  }, [])

  // Debounce search query (200ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 200)
    return () => clearTimeout(timer)
  }, [query])

  // Compute search results
  const results = useMemo(() => {
    if (!debouncedQuery.trim()) return []
    return searchElements(blocks, app, debouncedQuery.trim())
  }, [blocks, app, debouncedQuery])

  // Group results by block name
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {}
    for (const result of results) {
      if (!groups[result.blockName]) {
        groups[result.blockName] = []
      }
      groups[result.blockName].push(result)
    }
    return groups
  }, [results])

  // Handle clicking a search result
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      const store = useEditorStore.getState()

      // Switch to the page containing the block if needed
      if (result.pageId && result.pageId !== activePageId) {
        store.setActivePage(result.pageId)
      }

      // Select the element
      store.select(result.element.id, result.blockName)
    },
    [activePageId],
  )

  // Handle keyboard: Enter selects first result, Escape clears
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && results.length > 0) {
        e.preventDefault()
        handleResultClick(results[0])
      } else if (e.key === 'Escape') {
        if (query) {
          setQuery('')
        }
      }
    },
    [results, query, handleResultClick],
  )

  const clearSearch = useCallback(() => {
    setQuery('')
    inputRef.current?.focus()
  }, [])

  return (
    <div className="flex h-full flex-col border-r bg-background">
      {/* Header with back button */}
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <button
          onClick={() => useEditorStore.getState().setActiveRail('layers')}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Back to Outline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
        </button>
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-medium">Search</span>
      </div>

      {/* Search input */}
      <div className="relative px-3 py-2">
        <Search className="absolute left-5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          data-testid="search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search elements..."
          className="h-8 pl-7 pr-7 text-xs"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Result count */}
      {debouncedQuery.trim() && (
        <div className="px-3 pb-1 text-[11px] text-muted-foreground">
          {results.length} result{results.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Results */}
      <ScrollArea className="flex-1">
        <div className="px-2 pb-2">
          {debouncedQuery.trim() && results.length === 0 && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Search className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">No results found</p>
              <p className="text-[11px] text-muted-foreground/60">
                Try a different search term
              </p>
            </div>
          )}

          {!debouncedQuery.trim() && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Search className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">
                Search by element type, text, or ID
              </p>
              <p className="text-[11px] text-muted-foreground/60">
                Press Ctrl+F to focus search
              </p>
            </div>
          )}

          {Object.entries(groupedResults).map(([blockName, blockResults]) => (
            <div key={blockName} className="mt-1">
              {/* Block section header */}
              <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium text-muted-foreground">
                <HighlightMatch text={blockName} query={debouncedQuery} />
                <span className="text-muted-foreground/50">
                  ({blockResults.length})
                </span>
              </div>

              {/* Results in this block */}
              {blockResults.map((result) => (
                <SearchResultItem
                  key={`${result.blockName}-${result.element.id}`}
                  result={result}
                  query={debouncedQuery}
                  onClick={() => handleResultClick(result)}
                />
              ))}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SearchResultItem — Single result row
// ---------------------------------------------------------------------------

function SearchResultItem({
  result,
  query,
  onClick,
}: {
  result: SearchResult
  query: string
  onClick: () => void
}) {
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const isSelected = selectedIds.includes(result.element.id)

  return (
    <button
      data-testid="search-result"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded px-2 py-1 text-left transition-colors',
        isSelected
          ? 'bg-accent text-accent-foreground'
          : 'hover:bg-accent/50',
      )}
    >
      {/* Type badge */}
      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
        <HighlightMatch text={result.element.type} query={query} />
      </span>

      {/* Text content or ID */}
      <span className="min-w-0 flex-1 truncate text-xs">
        {result.element.text ? (
          <HighlightMatch
            text={
              result.element.text.length > 40
                ? result.element.text.slice(0, 40) + '...'
                : result.element.text
            }
            query={query}
          />
        ) : (
          <span className="font-mono text-[10px] text-muted-foreground/60">
            <HighlightMatch text={result.element.id} query={query} />
          </span>
        )}
      </span>

      {/* Truncated ID (always shown at end) */}
      {result.element.text && (
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground/40">
          {result.element.id.slice(0, 6)}
        </span>
      )}
    </button>
  )
}
